import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export default function ForgotPassword() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<'email' | 'reset'>('email');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  async function handleRequestReset(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const r = await fetch('/api/v1/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (r.status === 429) { setError('Too many requests — please wait a minute and try again.'); return; }
      if (!r.ok) { setError('Something went wrong. Please try again.'); return; }
      setStep('reset');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (newPassword !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      const r = await fetch('/api/v1/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, otp, new_password: newPassword }),
      });
      const data = await r.json();
      if (!r.ok) { setError(data.detail ?? 'Reset failed. Check your code and try again.'); return; }
      await refresh();
      nav('/profile');
    } catch {
      setError('Network error.');
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setResending(true);
    setResent(false);
    try {
      await fetch('/api/v1/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setResent(true);
    } finally {
      setResending(false);
    }
  }

  if (step === 'reset') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-sky-50 to-indigo-50 p-4">
        <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-lg">
          <button
            onClick={() => setStep('email')}
            className="mb-6 block text-xs text-slate-400 hover:text-slate-600"
          >
            ← Back
          </button>
          <h1 className="mb-1 text-2xl font-bold text-slate-900">Reset your password</h1>
          <p className="mb-6 text-sm text-slate-500">
            We sent a 6-digit code to <strong>{email}</strong>. Enter it with your new password.
          </p>
          <form onSubmit={handleReset} className="space-y-4">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="6-digit code"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-sky-500"
              required
            />
            <input
              type="password"
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              required
              minLength={8}
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              required
            />
            {error && <p className="text-sm text-rose-600">{error}</p>}
            <button
              type="submit"
              disabled={loading || otp.length < 6 || !newPassword || !confirm}
              className="w-full rounded-full bg-sky-600 py-3 font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:opacity-50"
            >
              {loading ? 'Resetting…' : 'Reset password'}
            </button>
          </form>
          <div className="mt-4 text-center">
            {resent ? (
              <p className="text-xs text-green-600">Code resent — check your inbox.</p>
            ) : (
              <button
                onClick={handleResend}
                disabled={resending}
                className="text-xs text-slate-400 hover:text-slate-600 disabled:opacity-50"
              >
                {resending ? 'Sending…' : "Didn't receive a code? Resend"}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-sky-50 to-indigo-50 p-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-lg">
        <Link to="/login" className="mb-6 block text-xs text-slate-400 hover:text-slate-600">← Back to sign in</Link>
        <h1 className="mb-1 text-2xl font-bold text-slate-900">Forgot your password?</h1>
        <p className="mb-6 text-sm text-slate-500">
          Enter your email and we'll send a reset code if an account exists.
        </p>
        <form onSubmit={handleRequestReset} className="space-y-4">
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            required
            autoComplete="email"
          />
          {error && <p className="text-sm text-rose-600">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-sky-600 py-3 font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:opacity-50"
          >
            {loading ? 'Sending…' : 'Send reset code'}
          </button>
        </form>
        <p className="mt-5 text-center text-sm text-slate-500">
          Remember your password?{' '}
          <Link to="/login" className="font-semibold text-sky-600 hover:text-sky-800">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
